import type {
  CurrencyCurrency,
  Form,
  LanguageCode,
  Product,
  SessionSession
} from '@linktivity/ars-neo';
import type { UnitCardUnit } from '@link/share/business';
import type { PlanState } from '@link/share/hooks/planState';
import {
  isOpenDatePlan,
  selectedUnitsToPlanUnitItems
} from '@link/share/utils';

/*
 * The session a booking submits. From share's `useProductPage`
 * (`buildSessionData`), which kept a `session` state alongside the plan
 * states and overwrote most of it here; this builds it from the plan state
 * alone, at the moment it is sent.
 */

export interface SessionInput {
  product: Product;
  languageCode: LanguageCode;
  currency: CurrencyCurrency;
  planState: PlanState;
  /** The selected units, priced for the selected day. */
  pricedUnits: UnitCardUnit[];
  optionForms: Form[];
  affiliateId: string | undefined;
}

export function buildSession({
  product,
  languageCode,
  currency,
  planState,
  pricedUnits,
  optionForms,
  affiliateId
}: SessionInput): SessionSession | undefined {
  const activity = product.productContent?.activity;
  const plan = activity?.plans?.find(p => p.id === planState.planId);
  if (!plan) return undefined;
  return {
    languageCode,
    productId: product.id ?? '',
    form: {
      productContentId: product.productContent?.id ?? '',
      activityForm: {
        activityId: activity?.id ?? '',
        activityTitle: activity?.title ?? '',
        thumbnailUrl: activity?.imageUrls?.[0] ?? '',
        displayCurrency: currency,
        planId: planState.planId,
        planTitle: plan.title ?? '',
        targetDate: planState.selectedDate,
        // An open-date plan's date is picked by the page, not the visitor, so
        // it is not a participation date.
        participationDate: isOpenDatePlan(plan)
          ? undefined
          : planState.selectedDate,
        planStartTimeId: planState.selectedTimeId,
        planStartTime: planState.selectedTimeTitle,
        planUnitItems: selectedUnitsToPlanUnitItems(pricedUnits)
      }
    },
    optionForms: optionForms.length
      ? optionForms.map(form => ({ ...form }))
      : undefined,
    affiliateId
  };
}
